/**
 * Final + Upscale zu EINEM Bild zusammensetzen (Regionen-Regler im LiveEditor).
 * Je Region wird das Final im überarbeiteten Rechteck weich ausgeblendet und der Upscale
 * eingesetzt:   ergebnis = final · (1 − m) + upscale · m
 * m = weiche Maske × `amount` (Anteil des Upscales: 0 = Überarbeitung voll sichtbar, 1 = nur Upscale).
 * Canvas rechnet intern mit vormultipliziertem Alpha — 'destination-out' + 'lighter' ergeben
 * genau diese Formel, auch bei transparenten Bildern.
 *
 * Bewusst ohne Abhängigkeiten (kein Vue, kein Firebase), damit die Funktion für sich testbar ist.
 *
 * @param {CanvasImageSource} finalImg  obere Ebene (nach dem Detail-Pass)
 * @param {CanvasImageSource} rawImg    untere Ebene (Upscale vor dem Pass), gleicher Bildausschnitt
 * Wo genau: Liefert die Region die Einsetz-Maske des Servers (`maskImg`), wird NUR deren Fläche
 * zurückgenommen — überlappende Nachbar-Regionen (Gruppenfotos) bleiben unberührt. Innerhalb der
 * Fläche gilt ein gleichmäßiger Anteil: final = upscale·(1−ms) + render·ms, also ist
 * upscale·a + final·(1−a) exakt „Render mit Deckkraft (1−a)". Ohne Maske (ältere Jobs) wird das
 * ganze Rechteck mit weichem Rand genommen.
 *
 * @param {{left:number, top:number, width:number, height:number, amount?:number, maskImg?:CanvasImageSource}[]} regions  Rechtecke in Pixeln der Bezugsgröße
 * @param {{refSize?: {width:number, height:number}, maxEdge?: number}} [opts]
 *        refSize = Bezugsgröße der Rechtecke (Standard: Größe des Finals);
 *        maxEdge = Ausgabe verkleinern (lange Kante) — schnelle Vorschau, Export läuft ohne
 * @returns {HTMLCanvasElement}
 */
/** Ab 1/SUPPORT_GAIN Maskenwert gilt eine Stelle voll als „vom Detail-Pass verändert" */
const SUPPORT_GAIN = 16;

export function compositeUpscaleLayers(finalImg, rawImg, regions, opts) {
  const options = opts || {};
  const fullW = finalImg.naturalWidth || finalImg.width;
  const fullH = finalImg.naturalHeight || finalImg.height;
  const rawW = rawImg.naturalWidth || rawImg.width;
  const rawH = rawImg.naturalHeight || rawImg.height;
  const k = options.maxEdge ? Math.min(1, options.maxEdge / Math.max(fullW, fullH)) : 1;
  const W = Math.max(1, Math.round(fullW * k));
  const H = Math.max(1, Math.round(fullH * k));
  const sx = W / ((options.refSize && options.refSize.width) || fullW);
  const sy = H / ((options.refSize && options.refSize.height) || fullH);

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(finalImg, 0, 0, W, H);

  for (const region of regions || []) {
    const amount = Math.max(0, Math.min(1, region.amount === undefined ? 1 : region.amount));
    if (amount <= 0) continue;
    const x = Math.max(0, Math.round(region.left * sx));
    const y = Math.max(0, Math.round(region.top * sy));
    const w = Math.min(W - x, Math.round(region.width * sx));
    const h = Math.min(H - y, Math.round(region.height * sy));
    if (w <= 0 || h <= 0) continue;

    const mask = document.createElement("canvas");
    mask.width = w;
    mask.height = h;
    const mctx = mask.getContext("2d");
    if (region.maskImg) {
      // Fläche der Server-Maske (dort wurde eingeblendet) → gleichmäßiger Anteil `amount`.
      // Steile Rampe statt harter Schwelle: am äußersten Rand ist final ≈ upscale, dort zählt es nicht.
      mctx.drawImage(region.maskImg, 0, 0, w, h);
      const data = mctx.getImageData(0, 0, w, h);
      const px = data.data;
      for (let i = 0; i < px.length; i += 4) {
        const support = Math.min(1, (px[i] / 255) * SUPPORT_GAIN);
        px[i] = px[i + 1] = px[i + 2] = 0;
        px[i + 3] = Math.round(255 * support * amount);
      }
      mctx.putImageData(data, 0, 0);
    } else {
      // Ältere Jobs ohne Maske: ganzes Rechteck, Rand läuft weich aus
      const feather = Math.max(2, Math.round(Math.min(w, h) * 0.05));
      mctx.globalAlpha = amount;
      if (typeof mctx.filter === "string") {
        mctx.filter = `blur(${feather}px)`;
        mctx.fillRect(feather * 1.5, feather * 1.5, w - feather * 3, h - feather * 3);
      } else {
        mctx.fillRect(0, 0, w, h); // ohne Canvas-Filter (alte Browser): harte Kante
      }
    }

    // Upscale-Ausschnitt, auf die Maske begrenzt
    const patch = document.createElement("canvas");
    patch.width = w;
    patch.height = h;
    const pctx = patch.getContext("2d");
    pctx.drawImage(rawImg, (x / W) * rawW, (y / H) * rawH, (w / W) * rawW, (h / H) * rawH, 0, 0, w, h);
    pctx.globalCompositeOperation = "destination-in";
    pctx.drawImage(mask, 0, 0);

    ctx.globalCompositeOperation = "destination-out";
    ctx.drawImage(mask, x, y);
    ctx.globalCompositeOperation = "lighter";
    ctx.drawImage(patch, x, y);
    ctx.globalCompositeOperation = "source-over";
  }
  return canvas;
}
